import { useClientConfig } from "@/contexts/ClientConfigContext";
import { isNomeValido, nomePessoaChave } from "@/lib/nomePessoa";
import { formatBRL } from "@/lib/formatBrl";
import { formatDisplayText } from "@/lib/formatDisplayText";
import { cardapioApi, CardapioApiError } from "@/lib/cardapio/api";
import { createIdempotencyKey } from "@/lib/cardapioOperationalNotifications";
import { categoryEntityId } from "@/lib/cardapio/translation";
import { useCardapioLocale } from "@/lib/cardapio/useCardapioLocale";
import type { CardapioLocale } from "@/lib/cardapio/i18n";
import { getAtendimentoLabels, limiteAtendimentoNumero } from "@/lib/atendimentoMode";
import {
  filterMesaInput,
  filterNomeInput,
  formatWhatsappMask,
  normalizeMesaDigits as normalizeMesaDigitsBase,
  parseMesaFromUrlParam as parseMesaFromUrlParamBase,
  whatsappDigits,
} from "@/lib/cardapio/formFields";
import {
  clearCardapioSessao,
  hasSessaoComandaAtiva,
  loadAtendimentoVinculo,
  loadCliente,
  loadMesaSessao as loadMesaSessaoBase,
  markSessaoComandaAtiva,
  saveAtendimentoVinculo,
  saveCliente,
  saveMesaSessao as saveMesaSessaoBase,
  type CardapioAtendimentoVinculo,
} from "@/lib/cardapio/storage";
import type {
  CardapioCatalogo,
  CardapioProduto,
  CardapioPromocao,
  CartItem,
  ClienteCardapio,
  ContaItem,
} from "@/lib/cardapio/types";
import { cartItemKey } from "@/lib/cardapio/types";
import { labelQuantidadeCaixa, unidadeVendaPermiteDecimal } from "@/lib/unidadeVenda";
import { ChevronDown, ChevronLeft, Loader2, MessageCircle, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "wouter";
import { toast } from "@/lib/appToast";
import {
  CardapioContaIcon,
  CardapioGarcomConfirmadoIcon,
  CardapioGarcomIcon,
} from "./CardapioIcons";
import "./cardapio.css";

const VIEW_CONTA_SESSAO_KEY = "cardapio_view_conta_sessao";

type View = "menu" | "cart" | "success" | "conta" | "aguarde" | "garcom-sent";
type GarcomModal = "info" | "options";

type CardapioModalItem = {
  produto_id: number;
  nome: string;
  descricao: string | null;
  imagem_url: string | null;
  preco_unitario: number;
  max_quantidade: number;
  unidade_venda: string;
};

const CARDAPIO_ICONS = {
  flagBr: "/imagens/br.svg",
  flagEua: "/imagens/eua.svg",
  flagSpain: "/imagens/spain.svg",
  adicionar: "/imagens/adicionar.svg",
} as const;

function categorySectionId(nome: string): string {
  return `cat-section-${encodeURIComponent(nome)}`;
}

function sortCardapioProdutosPorPreco(produtos: CardapioProduto[]): CardapioProduto[] {
  return produtos
    .map((produto, index) => ({ produto, index }))
    .sort((a, b) => {
      const precoA = a.produto.preco_venda ?? Number.POSITIVE_INFINITY;
      const precoB = b.produto.preco_venda ?? Number.POSITIVE_INFINITY;
      return precoA - precoB || a.index - b.index;
    })
    .map(({ produto }) => produto);
}

function SidebarCategoryCard({
  displayName,
  active,
  buttonRef,
  onClick,
}: {
  displayName: string;
  active: boolean;
  buttonRef: (node: HTMLButtonElement | null) => void;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      ref={buttonRef}
      className={`cardapio-sidebar-cat${active ? " cardapio-sidebar-cat--active" : ""}`}
      aria-current={active ? "true" : undefined}
      onClick={onClick}
    >
      <span className="cardapio-sidebar-cat-name">{formatDisplayText(displayName)}</span>
    </button>
  );
}

function CardapioAddBtn({
  className = "",
  onClick,
  label,
}: {
  className?: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      className={`cardapio-add-btn${className ? ` ${className}` : ""}`}
      onClick={onClick}
      aria-label={label}
    >
      <img src={CARDAPIO_ICONS.adicionar} alt="" />
    </button>
  );
}

function CardapioShell({
  children,
  innerClassName = "",
  innerStyle,
}: {
  children: React.ReactNode;
  innerClassName?: string;
  innerStyle?: React.CSSProperties;
}) {
  return (
    <div className="cardapio-page">
      <div className={`cardapio-inner${innerClassName ? ` ${innerClassName}` : ""}`} style={innerStyle}>
        {children}
      </div>
    </div>
  );
}

function roundQty(n: number, permiteDecimal: boolean): number {
  if (permiteDecimal) return Math.round(n * 100) / 100;
  return Math.round(n);
}

function contaItensSignature(itens: ContaItem[]): string {
  return JSON.stringify(
    itens.map((item) => [
      item.id,
      item.produto_id,
      item.nome_produto,
      item.observacao ?? "",
      Number(item.quantidade),
      Number(item.preco_unitario),
      Number(item.subtotal),
    ]),
  );
}

function QtyControl({
  value,
  min = 1,
  max,
  onChange,
  decreaseLabel,
  increaseLabel,
  permiteDecimal = false,
}: {
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
  decreaseLabel: string;
  increaseLabel: string;
  permiteDecimal?: boolean;
}) {
  const step = permiteDecimal ? 0.01 : 1;
  const showDecrease = value > min;
  const showIncrease = max == null || value < max - 0.0001;
  const display = permiteDecimal ? value.toFixed(2) : String(value);

  const bump = (delta: number) => {
    let next = roundQty(value + delta, permiteDecimal);
    if (max != null) next = Math.min(max, next);
    if (next < min) next = min;
    onChange(next);
  };

  return (
    <div className={`cardapio-qty-control${showDecrease ? "" : " cardapio-qty-control--no-decrease"}`}>
      {showDecrease ? (
        <button type="button" onClick={() => bump(-step)} aria-label={decreaseLabel}>
          -
        </button>
      ) : null}
      <span>{display}</span>
      {showIncrease ? (
        <button
          type="button"
          onClick={() => bump(step)}
          aria-label={increaseLabel}
        >
          +
        </button>
      ) : null}
    </div>
  );
}

export default function CardapioPage() {
  const params = useParams<{ mesa?: string }>();
  const { config: clientConfig } = useClientConfig();
  const modo = clientConfig.modo_atendimento;
  const storageScope = clientConfig.client_id || clientConfig.nome_empresa || "grandsystem";
  const normalizeMesaDigits = useCallback((value: string) => normalizeMesaDigitsBase(value, modo), [modo]);
  const parseMesaFromUrlParam = useCallback((value?: string) => parseMesaFromUrlParamBase(value, modo), [modo]);
  const loadMesaSessao = useCallback(() => loadMesaSessaoBase(modo, storageScope), [modo, storageScope]);
  const saveMesaSessao = useCallback((value: string) => saveMesaSessaoBase(value, modo, storageScope), [modo, storageScope]);
  const [loading, setLoading] = useState(true);
  const [catalogo, setCatalogo] = useState<CardapioCatalogo | null>(null);
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());
  const [view, setView] = useState<View>(() => {
    try {
      if (sessionStorage.getItem(VIEW_CONTA_SESSAO_KEY) === "conta" && loadMesaSessao()) {
        return "conta";
      }
    } catch {
      /* ignore */
    }
    return "menu";
  });
  const [cart, setCart] = useState<CartItem[]>([]);
  const [modalItem, setModalItem] = useState<CardapioModalItem | null>(null);
  const [garcomModal, setGarcomModal] = useState<GarcomModal | null>(null);
  const [modalQty, setModalQty] = useState(1);
  const [modalObs, setModalObs] = useState("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [cliente, setCliente] = useState<ClienteCardapio>(() => {
    const saved = loadCliente(storageScope);
    if (!saved) return { nome: "", whatsapp: "" };
    return { nome: saved.nome, whatsapp: formatWhatsappMask(saved.whatsapp) };
  });
  const mesaUserEditedRef = useRef(false);
  const [mesaInput, setMesaInput] = useState(() => {
    const fromUrl = parseMesaFromUrlParam(params.mesa);
    if (fromUrl) {
      saveMesaSessao(fromUrl);
      return fromUrl;
    }
    return loadMesaSessao();
  });
  const [atendimentoVinculo, setAtendimentoVinculo] = useState<CardapioAtendimentoVinculo | null>(() =>
    loadAtendimentoVinculo(modo, storageScope),
  );
  const [contaItens, setContaItens] = useState<ContaItem[]>([]);
  const [selectedContaIds, setSelectedContaIds] = useState<Set<number>>(new Set());
  const [contaLoading, setContaLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [desktopCategoryNav, setDesktopCategoryNav] = useState(false);
  const sidebarRef = useRef<HTMLElement | null>(null);
  const sidebarCategoryRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const categorySectionRefs = useRef<Map<string, HTMLElement>>(new Map());
  const contaSeenItemIdsRef = useRef<Set<number>>(new Set());
  const contaItensSignatureRef = useRef("");
  const contaIdentSignatureRef = useRef("");

  const [garcomNome, setGarcomNome] = useState("");
  const [garcomMesa, setGarcomMesa] = useState("");
  const [garcomCopos, setGarcomCopos] = useState(0);
  const [garcomTalheres, setGarcomTalheres] = useState(0);
  const [garcomPratos, setGarcomPratos] = useState(0);
  const [garcomLimpar, setGarcomLimpar] = useState(false);
  const [garcomOutros, setGarcomOutros] = useState("");
  const [garcomSending, setGarcomSending] = useState(false);

  const {
    locale,
    translating,
    selectLocale,
    t,
    td,
    empresaNomeDisplay,
    resolveCartNome,
    resolveModalTexts,
  } = useCardapioLocale(catalogo, clientConfig.modo_atendimento);

  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.quantidade, 0), [cart]);
  const empresaNome = formatDisplayText(empresaNomeDisplay || catalogo?.empresa.nome || "Nome da Empresa");
  const empresaLogo = catalogo?.empresa.logo_url;

  const modalDisplay = useMemo(
    () => (modalItem ? resolveModalTexts(modalItem) : null),
    [modalItem, resolveModalTexts],
  );

  const flagLocales: { code: CardapioLocale; src: string; label: string }[] = [
    { code: "pt", src: CARDAPIO_ICONS.flagBr, label: "Português" },
    { code: "en", src: CARDAPIO_ICONS.flagEua, label: "English" },
    { code: "es", src: CARDAPIO_ICONS.flagSpain, label: "Español" },
  ];

  const mesaNormalizada = useMemo(() => normalizeMesaDigits(mesaInput), [mesaInput, modo, normalizeMesaDigits]);
  const mesaRotaNormalizada = useMemo(() => parseMesaFromUrlParam(params.mesa), [params.mesa, modo, parseMesaFromUrlParam]);
  const clienteNomeNormalizado = cliente.nome.trim();
  const clienteNomeValido = !clienteNomeNormalizado || isNomeValido(clienteNomeNormalizado);
  const clienteIdentificacaoValida = clienteNomeValido && Boolean(clienteNomeNormalizado || mesaNormalizada);
  const [atendimentoLookup, setAtendimentoLookup] = useState<{
    key: string;
    existe: boolean;
    comanda_id?: number;
    mesa?: string;
    cliente_nome?: string;
  } | null>(null);
  const atendimentoKey = JSON.stringify([modo, mesaNormalizada, nomePessoaChave(cliente.nome)]);
  const atendimentoVinculoAlterado = Boolean(
    atendimentoVinculo &&
      (normalizeMesaDigits(atendimentoVinculo.mesa) !== mesaNormalizada ||
        nomePessoaChave(atendimentoVinculo.nome) !== nomePessoaChave(cliente.nome)),
  );
  const deveVerificarAtendimento = !atendimentoVinculo || atendimentoVinculoAlterado;
  const atendimentoVerificado = !deveVerificarAtendimento || atendimentoLookup?.key === atendimentoKey;
  const atendimentoExistente =
    atendimentoLookup?.key === atendimentoKey &&
    atendimentoLookup.existe &&
    atendimentoLookup.comanda_id !== atendimentoVinculo?.comanda_id;
  useEffect(() => {
    setAtendimentoLookup(null);
    if (!checkoutOpen || !clienteIdentificacaoValida || !deveVerificarAtendimento) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void cardapioApi.buscarAtendimentoAberto(mesaNormalizada, cliente.nome.trim(), controller.signal)
        .then((lookup) => {
          if (!controller.signal.aborted) setAtendimentoLookup({ key: atendimentoKey, ...lookup });
        })
        .catch((error) => {
          if (!controller.signal.aborted) toast.error(error instanceof Error ? error.message : t("errors.sendOrder"));
        });
    }, 300);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [checkoutOpen, clienteIdentificacaoValida, deveVerificarAtendimento, atendimentoKey, mesaNormalizada, cliente.nome, t]);
  const garcomNomeNormalizado = garcomNome.trim();
  const garcomMesaNormalizada = normalizeMesaDigits(garcomMesa);
  const garcomNomeValido = !garcomNomeNormalizado || isNomeValido(garcomNomeNormalizado);
  const garcomIdentificacaoValida = garcomNomeValido && Boolean(garcomNomeNormalizado || garcomMesaNormalizada);
  const currentFlagLocale = flagLocales.find((item) => item.code === locale) ?? flagLocales[0];

  const setSidebarCategoryRef = useCallback((nome: string, node: HTMLButtonElement | null) => {
    if (node) {
      sidebarCategoryRefs.current.set(nome, node);
      return;
    }
    sidebarCategoryRefs.current.delete(nome);
  }, []);

  const handleClienteNomeChange = (nome: string) => {
    setCliente((c) => ({ ...c, nome: filterNomeInput(nome).slice(0, 10) }));
  };

  const handleMesaChange = (value: string) => {
    mesaUserEditedRef.current = true;
    const filtered = filterMesaInput(value, modo);
    setMesaInput(filtered);
    saveMesaSessao(filtered);
  };

  const handleMesaBlur = () => {
    const normalized = normalizeMesaDigits(mesaInput);
    setMesaInput(normalized);
    saveMesaSessao(normalized);
  };

  // Prefill da URL só enquanto o cliente não alterou o campo Mesa manualmente.
  useEffect(() => {
    if (mesaUserEditedRef.current) return;
    const fromUrl = parseMesaFromUrlParam(params.mesa) || loadMesaSessao();
    if (!fromUrl) return;
    setMesaInput((cur) => (cur === fromUrl ? cur : fromUrl));
    saveMesaSessao(fromUrl);
  }, [params.mesa, modo, parseMesaFromUrlParam, saveMesaSessao, loadMesaSessao]);

  const limparSessaoComandaFinalizada = useCallback(() => {
    clearCardapioSessao(storageScope);
    try {
      sessionStorage.removeItem(VIEW_CONTA_SESSAO_KEY);
    } catch {
      /* ignore */
    }
    setMesaInput("");
    setCliente({ nome: "", whatsapp: "" });
    setCart([]);
    setAtendimentoVinculo(null);
    setContaItens([]);
    contaSeenItemIdsRef.current = new Set();
    contaItensSignatureRef.current = "";
    contaIdentSignatureRef.current = "";
    setSelectedContaIds(new Set());
    setCheckoutOpen(false);
    setView("menu");
  }, [storageScope]);

  const verificarComandaFinalizada = useCallback(async () => {
    const vinculo = atendimentoVinculo ?? loadAtendimentoVinculo(modo, storageScope);
    if (!mesaNormalizada && !vinculo?.comanda_id) return false;
    if (checkoutOpen || view === "cart") return false;
    if (!hasSessaoComandaAtiva(storageScope) && view !== "conta" && view !== "aguarde") return false;
    try {
      if (vinculo?.comanda_id) {
        const conta = await cardapioApi.conta({ comanda_id: vinculo.comanda_id });
        if (conta.atendimento_aberto === false) {
          limparSessaoComandaFinalizada();
          return true;
        }
        return false;
      }
      const st = await cardapioApi.status({ mesa: mesaNormalizada });
      if (st.mesa_finalizada) {
        limparSessaoComandaFinalizada();
        return true;
      }
    } catch {
      /* ignore poll errors */
    }
    return false;
  }, [atendimentoVinculo, checkoutOpen, limparSessaoComandaFinalizada, mesaNormalizada, modo, storageScope, view]);

  const carregarCatalogo = useCallback(async () => {
    const data = await cardapioApi.catalogo();
    setCatalogo(data);
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        await carregarCatalogo();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : t("errors.loadCatalog"));
      } finally {
        setLoading(false);
      }
    })();
  }, [carregarCatalogo, t]);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => setDesktopCategoryNav(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const toggleCategory = (nome: string) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(nome)) next.delete(nome);
      else next.add(nome);
      return next;
    });
  };

  const scrollToCategory = useCallback((nome: string) => {
    const el = document.getElementById(categorySectionId(nome));
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    if (!desktopCategoryNav || view !== "menu" || !catalogo?.categorias.length) return;

    let rafId = 0;
    const visibleEntries = new Map<string, IntersectionObserverEntry>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const category = entry.target.getAttribute("data-category-name");
          if (!category) return;
          if (entry.isIntersecting) visibleEntries.set(category, entry);
          else visibleEntries.delete(category);
        });

        if (rafId) window.cancelAnimationFrame(rafId);
        rafId = window.requestAnimationFrame(() => {
          let nextCategory = activeCategory || catalogo.categorias[0]?.nome || "";
          let bestTop = Number.POSITIVE_INFINITY;
          visibleEntries.forEach((entry, category) => {
            const top = Math.abs(entry.boundingClientRect.top);
            if (top < bestTop) {
              bestTop = top;
              nextCategory = category;
            }
          });
          if (nextCategory) setActiveCategory(nextCategory);
        });
      },
      { root: null, rootMargin: "-20% 0px -55% 0px", threshold: [0, 0.2, 0.6] },
    );

    catalogo.categorias.forEach((cat) => {
      const section = categorySectionRefs.current.get(cat.nome);
      if (section) observer.observe(section);
    });

    return () => {
      if (rafId !== 0) {
        window.cancelAnimationFrame(rafId);
      }
      observer.disconnect();
      visibleEntries.clear();
    };
  }, [activeCategory, catalogo, desktopCategoryNav, view]);

  useEffect(() => {
    if (!desktopCategoryNav || !activeCategory) return;
    const sidebar = sidebarRef.current;
    const activeButton = sidebarCategoryRefs.current.get(activeCategory);
    if (!sidebar || !activeButton) return;

    const sidebarRect = sidebar.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();
    const isAbove = buttonRect.top < sidebarRect.top;
    const isBelow = buttonRect.bottom > sidebarRect.bottom;

    if (!isAbove && !isBelow) return;

    const targetTop = activeButton.offsetTop - (sidebar.clientHeight - activeButton.offsetHeight) / 2;
    sidebar.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
  }, [activeCategory, desktopCategoryNav]);

  const openProduct = (p: CardapioProduto) => {
    const uv = p.unidade_venda ?? "unidade";
    setModalItem({
      produto_id: p.id,
      nome: p.nome,
      descricao: p.descricao,
      imagem_url: p.imagem_url,
      preco_unitario: p.preco_venda ?? 0,
      max_quantidade: p.max_quantidade,
      unidade_venda: uv,
    });
    setModalQty(1);
    setModalObs("");
  };

  const openPromo = (promo: CardapioPromocao) => {
    if (!promo.produto_id) {
      toast.error(t("errors.promoUnavailable"));
      return;
    }
    setModalItem({
      produto_id: promo.produto_id,
      nome: promo.nome,
      descricao: promo.subtitulo || null,
      imagem_url: null,
      preco_unitario: promo.valor,
      max_quantidade: promo.max_quantidade > 0 ? promo.max_quantidade : 1,
      unidade_venda: promo.unidade_venda ?? "unidade",
    });
    setModalQty(1);
    setModalObs("");
  };

  const addToCart = () => {
    if (!modalItem) return;
    const permiteDecimal = unidadeVendaPermiteDecimal(modalItem.unidade_venda);
    const qty = roundQty(modalQty, permiteDecimal);
    if (permiteDecimal ? qty <= 0 : qty < 1) return;
    const obs = modalObs.trim();
    const key = cartItemKey(modalItem.produto_id, obs);
    setCart((prev) => {
      const idx = prev.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const copy = [...prev];
        const nextQty = roundQty(copy[idx].quantidade + qty, permiteDecimal);
        if (nextQty > modalItem.max_quantidade + 0.0001) {
          toast.error(t("errors.qtyOverStock"));
          return prev;
        }
        copy[idx] = { ...copy[idx], quantidade: nextQty };
        return copy;
      }
      return [
        ...prev,
        {
          key,
          produto_id: modalItem.produto_id,
          nome: modalItem.nome,
          descricao: modalItem.descricao,
          imagem_url: modalItem.imagem_url,
          preco_unitario: modalItem.preco_unitario,
          quantidade: qty,
          observacao: obs,
          max_quantidade: modalItem.max_quantidade,
          unidade_venda: modalItem.unidade_venda,
        },
      ];
    });
    setModalItem(null);
  };

  const updateCartQty = (key: string, qty: number) => {
    setCart((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, quantidade: qty } : i))
        .filter((i) => i.quantidade > 0),
    );
  };

  const removeCartItem = (key: string) => {
    setCart((prev) => prev.filter((i) => i.key !== key));
  };

  const handleEnviarPedido = async () => {
    const mesa = normalizeMesaDigits(mesaInput);
    const nome = cliente.nome.trim();
    const vinculoAtual = atendimentoVinculo;
    if (sending || (!vinculoAtual && !atendimentoVerificado)) return;
    if (!clienteIdentificacaoValida) {
      toast.error(t("errors.fillNameWhatsappTable"));
      return;
    }
    if (cart.length === 0) return;
    const whatsappSalvo = whatsappDigits(cliente.whatsapp);
    const atendimentoEncontradoId =
      atendimentoLookup?.key === atendimentoKey && atendimentoLookup.existe ? Number(atendimentoLookup.comanda_id || 0) : 0;
    const comandaIdDestino =
      atendimentoEncontradoId > 0 && atendimentoEncontradoId !== vinculoAtual?.comanda_id
        ? atendimentoEncontradoId
        : vinculoAtual?.comanda_id;
    try {
      setSending(true);
      const res = await cardapioApi.enviarPedido({
        mesa,
        cliente_nome: nome,
        comanda_id: comandaIdDestino,
        confirmar_adicao: Boolean(comandaIdDestino || atendimentoExistente),
        itens: cart.map((i) => ({
          produto_id: i.produto_id,
          quantidade: i.quantidade,
          observacao: i.observacao,
        })),
      });
      saveCliente({ nome, whatsapp: whatsappSalvo }, storageScope);
      const nextMesa = res.mesa || mesa;
      setMesaInput(nextMesa);
      saveMesaSessao(nextMesa);
      const nextVinculo = { comanda_id: res.comanda_id, nome, mesa: nextMesa };
      saveAtendimentoVinculo(nextVinculo, modo, storageScope);
      setAtendimentoVinculo(nextVinculo);
      markSessaoComandaAtiva(storageScope);
      setCart([]);
      setCheckoutOpen(false);
      setView("success");
      void carregarCatalogo();
    } catch (e) {
      if (e instanceof CardapioApiError && e.status === 409) {
        setAtendimentoLookup({ key: atendimentoKey, existe: true });
        return;
      }
      toast.error(e instanceof Error ? e.message : t("errors.sendOrder"));
    } finally {
      setSending(false);
    }
  };

  const carregarConta = useCallback(
    async (opts?: { silent?: boolean }) => {
      const vinculo = atendimentoVinculo ?? loadAtendimentoVinculo(modo, storageScope);
      if (!vinculo?.comanda_id) {
        setContaLoading(false);
        if (contaItensSignatureRef.current !== "") {
          setContaItens([]);
          setSelectedContaIds(new Set());
        }
        contaSeenItemIdsRef.current = new Set();
        contaItensSignatureRef.current = "";
        contaIdentSignatureRef.current = "";
        return;
      }

      const isInitialLoad = contaItensSignatureRef.current === "";
      if (!opts?.silent && isInitialLoad) setContaLoading(true);
      try {
        const data = await cardapioApi.conta({
          comanda_id: vinculo.comanda_id,
          cliente_nome: cliente.nome.trim(),
          cliente_whatsapp: whatsappDigits(cliente.whatsapp),
        });
        if (data.atendimento_aberto === false) {
          limparSessaoComandaFinalizada();
          return;
        }

        const itensConta = data.itens ?? data.itens_mesa ?? [];
        const nextNome = (data.cliente_nome ?? vinculo.nome ?? "").trim();
        const nextMesa = normalizeMesaDigits(data.mesa ?? vinculo.mesa);
        const nextVinculo = { comanda_id: vinculo.comanda_id, nome: nextNome, mesa: nextMesa };
        const nextIdentSignature = JSON.stringify(nextVinculo);
        const nextItensSignature = contaItensSignature(itensConta);

        if (nextIdentSignature !== contaIdentSignatureRef.current) {
          contaIdentSignatureRef.current = nextIdentSignature;
          saveAtendimentoVinculo(nextVinculo, modo, storageScope);
          setAtendimentoVinculo((cur) => {
            if (
              cur?.comanda_id === nextVinculo.comanda_id &&
              cur.nome === nextVinculo.nome &&
              cur.mesa === nextVinculo.mesa
            ) {
              return cur;
            }
            return nextVinculo;
          });
          if (nextNome) setCliente((cur) => (cur.nome === nextNome ? cur : { ...cur, nome: nextNome }));
          if (nextMesa) {
            setMesaInput((cur) => (cur === nextMesa ? cur : nextMesa));
            saveMesaSessao(nextMesa);
          }
        }

        if (nextItensSignature !== contaItensSignatureRef.current) {
          const previousSeenIds = contaSeenItemIdsRef.current;
          const validIds = new Set(itensConta.map((i) => i.id));
          setContaItens(itensConta);
          setSelectedContaIds((prev) => {
            const kept = new Set<number>();
            prev.forEach((id) => {
              if (validIds.has(id)) kept.add(id);
            });
            itensConta.forEach((item) => {
              if (!previousSeenIds.has(item.id)) kept.add(item.id);
            });
            return kept;
          });
          contaSeenItemIdsRef.current = validIds;
          contaItensSignatureRef.current = nextItensSignature;
        }
      } catch (e) {
        if (!opts?.silent && isInitialLoad) {
          toast.error(e instanceof Error ? e.message : t("errors.loadAccount"));
        } else if (import.meta.env.DEV) {
          console.warn("[cardapio:conta] Falha na sincronizacao silenciosa", e);
        }
      } finally {
        if (!opts?.silent && isInitialLoad) setContaLoading(false);
      }
    },
    [atendimentoVinculo, cliente.nome, cliente.whatsapp, limparSessaoComandaFinalizada, modo, normalizeMesaDigits, saveMesaSessao, storageScope, t],
  );
  const mesaContaAnteriorRef = useRef(mesaNormalizada);

  useEffect(() => {
    if (mesaContaAnteriorRef.current !== mesaNormalizada) {
      mesaContaAnteriorRef.current = mesaNormalizada;
    }
  }, [mesaNormalizada]);

  useEffect(() => {
    const nome = cliente.nome.trim();
    const zap = whatsappDigits(cliente.whatsapp);
    if (nome && zap) {
      saveCliente({ nome, whatsapp: zap }, storageScope);
    }
  }, [cliente.nome, cliente.whatsapp]);

  useEffect(() => {
    if (view === "conta") {
      try {
        sessionStorage.setItem(VIEW_CONTA_SESSAO_KEY, "conta");
      } catch {
        /* ignore */
      }
      void carregarConta();
      return;
    }
    try {
      sessionStorage.removeItem(VIEW_CONTA_SESSAO_KEY);
    } catch {
      /* ignore */
    }
  }, [view, carregarConta]);

  useEffect(() => {
    if (view !== "conta") return;
    const syncConta = () => {
      if (document.visibilityState === "visible") {
        void carregarConta({ silent: true });
      }
    };
    const id = window.setInterval(syncConta, 12000);
    document.addEventListener("visibilitychange", syncConta);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", syncConta);
    };
  }, [view, carregarConta]);

  useEffect(() => {
    const vinculo = loadAtendimentoVinculo(modo, storageScope);
    setAtendimentoVinculo(vinculo);
    if (vinculo?.mesa) {
      setMesaInput(vinculo.mesa);
      saveMesaSessao(vinculo.mesa);
    }
    if (vinculo?.nome) {
      setCliente((cur) => ({ ...cur, nome: vinculo.nome }));
    }
  }, [modo, saveMesaSessao]);

  useEffect(() => {
    if (view === "conta") return;
    const mesaParaStatus = atendimentoVinculo?.mesa || mesaNormalizada;
    if (!mesaParaStatus && !atendimentoVinculo?.comanda_id) return;
    void verificarComandaFinalizada();
    const id = window.setInterval(() => void verificarComandaFinalizada(), 5000);
    return () => window.clearInterval(id);
  }, [atendimentoVinculo?.comanda_id, atendimentoVinculo?.mesa, mesaNormalizada, verificarComandaFinalizada, view]);

  const cardapioModalAberto = Boolean(modalItem || checkoutOpen || garcomModal);

  useEffect(() => {
    if (!cardapioModalAberto) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [cardapioModalAberto]);

  const selectedSubtotal = useMemo(() => {
    return contaItens
      .filter((i) => selectedContaIds.has(i.id))
      .reduce((s, i) => s + i.subtotal, 0);
  }, [contaItens, selectedContaIds]);

  const handleFecharConta = async () => {
    const itensSelecionados = contaItens.filter((item) => selectedContaIds.has(item.id));
    const totalSelecionado = itensSelecionados.reduce((s, i) => s + i.subtotal, 0);
    if (!atendimentoVinculo || itensSelecionados.length === 0 || totalSelecionado <= 0) {
      toast.error(t("errors.selectItems"));
      return;
    }

    try {
      setSending(true);
      await cardapioApi.solicitarFecharConta({
        cliente_nome: (atendimentoVinculo.nome || cliente.nome).trim(),
        cliente_telefone: whatsappDigits(cliente.whatsapp),
        mesa: atendimentoVinculo.mesa || mesaNormalizada,
        comanda_id: atendimentoVinculo.comanda_id,
        idempotency_key: createIdempotencyKey(),
        modo: "selecionados",
        item_ids: itensSelecionados.map((item) => item.id),
        valor_total: totalSelecionado,
        pagamento: "no_local",
      });
      setView("aguarde");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("errors.closeAccount"));
    } finally {
      setSending(false);
    }
  };

  const openConta = () => {
    const vinculo = atendimentoVinculo ?? loadAtendimentoVinculo(modo, storageScope);
    if (vinculo) {
      setAtendimentoVinculo(vinculo);
      if (vinculo.nome) setCliente((cur) => ({ ...cur, nome: vinculo.nome }));
      if (vinculo.mesa) setMesaInput(vinculo.mesa);
    }
    setView("conta");
  };

  const openGarcom = () => {
    const saved = loadCliente(storageScope);
    setGarcomNome(saved?.nome || atendimentoVinculo?.nome || cliente.nome);
    setGarcomMesa(atendimentoVinculo?.mesa || mesaNormalizada || loadMesaSessao());
    setGarcomModal("info");
  };

  if (loading) {
    return (
      <div className="cardapio-page">
        <div className="cardapio-loading">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  if (view === "success") {
    return (
      <div className="cardapio-page">
        <div className="cardapio-status-screen cardapio-inner">
          <CardapioGarcomConfirmadoIcon className="cardapio-status-icon" />
          <h2>{t("success.orderTitle")}</h2>
          <div className="cardapio-footer-fixed">
            <button type="button" className="cardapio-btn-primary" onClick={() => setView("menu")}>
              {t("success.backToMenu")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === "aguarde") {
    return (
      <div className="cardapio-page">
        <div className="cardapio-status-screen cardapio-inner">
          <h2>{t("wait.waiter")}</h2>
          <button type="button" className="cardapio-btn-primary" onClick={() => setView("menu")}>
            {t("common.back")}
          </button>
        </div>
      </div>
    );
  }

  if (view === "garcom-sent") {
    return (
      <div className="cardapio-page">
        <div className="cardapio-status-screen cardapio-inner">
          <CardapioGarcomIcon className="cardapio-status-icon" />
          <h2>{t("wait.requestSent")}</h2>
          <div className="cardapio-footer-fixed">
            <button type="button" className="cardapio-btn-primary" onClick={() => setView("menu")}>
              {t("success.backToMenu")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === "cart") {
    const cartSubtotal = cart.reduce((s, i) => s + i.preco_unitario * i.quantidade, 0);
    const cartTotal = cartSubtotal;

    return (
      <CardapioShell innerClassName={cart.length > 0 ? "cardapio-inner--footer-pad" : undefined}>
        <header className="cardapio-screen-header cardapio-screen-header--flat">
          <button type="button" className="cardapio-back" onClick={() => setView("menu")}>
            <ChevronLeft className="h-4 w-4" /> {t("common.back")}
          </button>
          <h1 className="cardapio-screen-title">{t("cart.title")}</h1>
        </header>
        <h2 style={{ padding: "0.75rem 1rem 0", fontSize: "18px", fontWeight: 700 }}>{t("cart.itemsSection")}</h2>
        {cart.length === 0 ? (
          <p style={{ padding: "1rem", color: "var(--cardapio-muted)", fontSize: "14px" }}>{t("cart.empty")}</p>
        ) : (
          cart.map((item) => (
            <div key={item.key} className="cardapio-cart-item">
              {item.imagem_url ? (
                <img src={item.imagem_url} alt="" className="cardapio-product-thumb" />
              ) : null}
              <div className="cardapio-cart-item-info">
                <p className="cardapio-cart-item-name">{formatDisplayText(resolveCartNome(item))}</p>
                {item.observacao ? <p className="cardapio-cart-item-obs">{item.observacao}</p> : null}
              </div>
              <div className="cardapio-cart-controls">
                <QtyControl
                  value={item.quantidade}
                  min={unidadeVendaPermiteDecimal(item.unidade_venda) ? 0.01 : 1}
                  max={item.max_quantidade}
                  permiteDecimal={unidadeVendaPermiteDecimal(item.unidade_venda)}
                  onChange={(q) => updateCartQty(item.key, q)}
                  decreaseLabel={t("common.decrease")}
                  increaseLabel={t("common.increase")}
                />
                <button type="button" className="cardapio-trash" onClick={() => removeCartItem(item.key)} aria-label={t("common.remove")}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
        {cart.length > 0 ? (
          <div className="cardapio-footer-fixed">
            <div className="cardapio-conta-line">
              <span>{t("common.subtotal")}</span>
              <span>{formatBRL(cartSubtotal)}</span>
            </div>
            <div className="cardapio-conta-line total">
              <span>{t("common.total")}</span>
              <span>{formatBRL(cartTotal)}</span>
            </div>
            <button
              type="button"
              className="cardapio-btn-primary"
              onClick={() => {
                const saved = loadCliente(storageScope);
                if (saved) {
                  setCliente({ nome: saved.nome, whatsapp: formatWhatsappMask(saved.whatsapp) });
                }
                if (atendimentoVinculo?.nome) {
                  setCliente((cur) => ({ ...cur, nome: atendimentoVinculo.nome }));
                }
                if (atendimentoVinculo?.mesa) {
                  setMesaInput(atendimentoVinculo.mesa);
                }
                setCheckoutOpen(true);
              }}
            >
              {t("cart.placeOrder")}
            </button>
          </div>
        ) : null}
        {checkoutOpen ? (
          <div className="cardapio-overlay">
            <div className="cardapio-modal">
              <button type="button" className="cardapio-modal-close" onClick={() => setCheckoutOpen(false)} aria-label={t("common.close")}>
                ×
              </button>
              <h2 className="cardapio-modal-title">{t("cart.checkoutTitle")}</h2>
              <p className="cardapio-modal-sub">{t("cart.checkoutSub")}</p>
              <div className="cardapio-field">
                <div className="flex items-center justify-between gap-3">
                  <label htmlFor="c_nome">{t("common.name")}</label>
                  <span className="text-xs text-zinc-400">{cliente.nome.length}/10</span>
                </div>
                <input
                  id="c_nome"
                  maxLength={10}
                  value={cliente.nome}
                  onChange={(e) => handleClienteNomeChange(e.target.value)}
                />
              </div>
              <div className="cardapio-field">
                <label htmlFor="c_mesa">{t("common.tableNumber")}</label>
                <input
                  id="c_mesa"
                  inputMode="numeric"
                  maxLength={limiteAtendimentoNumero(modo)}
                  value={mesaInput}
                  onChange={(e) => handleMesaChange(e.target.value)}
                  onBlur={handleMesaBlur}
                />
              </div>
              {atendimentoExistente ? <p className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">{t("cart.existingTable")}</p> : null}
              <button type="button" className="cardapio-btn-primary" disabled={sending || !clienteIdentificacaoValida || !atendimentoVerificado} onClick={() => void handleEnviarPedido()}>
                {sending ? t("common.sending") : atendimentoExistente ? t("cart.addToTable") : t("cart.sendKitchen")}
              </button>
            </div>
          </div>
        ) : null}
      </CardapioShell>
    );
  }

  if (view === "conta") {
    const subtotal = selectedSubtotal;
    const total = subtotal;
    const temProdutosNaConta = contaItens.length > 0;
    const contaVazia = !temProdutosNaConta;
    const labels = getAtendimentoLabels(modo);
    const contaNome = (atendimentoVinculo?.nome || cliente.nome).trim();
    const contaNumero = atendimentoVinculo?.mesa || mesaNormalizada;

    return (
      <CardapioShell>
        <header className="cardapio-screen-header cardapio-screen-header--flat">
          <button type="button" className="cardapio-back" onClick={() => setView("menu")}>
            <ChevronLeft className="h-4 w-4" /> {t("common.back")}
          </button>
          <h1 className="cardapio-screen-title">{t("account.closeAccount")}</h1>
        </header>

        {atendimentoVinculo ? (
          <div className="cardapio-conta-ident-grid">
            {contaNumero ? (
              <div className="cardapio-conta-ident-card">
                <span>{labels.singular}</span>
                <strong>{contaNumero}</strong>
              </div>
            ) : null}
            {contaNome ? (
              <div className="cardapio-conta-ident-card">
                <span>Nome</span>
                <strong>{contaNome}</strong>
              </div>
            ) : null}
          </div>
        ) : null}

        <h2 className="cardapio-conta-section-title">Pedidos</h2>
        {contaLoading ? (
          <div className="cardapio-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="cardapio-conta-list">
            {contaVazia ? (
              <p className="cardapio-conta-empty-text">{t("account.empty")}</p>
            ) : (
              contaItens.map((item) => (
                <div key={item.id} className="cardapio-conta-item">
                  <input
                    type="checkbox"
                    className="cardapio-checkbox"
                    checked={selectedContaIds.has(item.id)}
                    onChange={(e) => {
                      setSelectedContaIds((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(item.id);
                        else next.delete(item.id);
                        return next;
                      });
                    }}
                  />
                  <label>
                    <span className="cardapio-conta-item-name">
                      {Math.round(item.quantidade) === item.quantidade ? item.quantidade : item.quantidade.toFixed(2)}x{" "}
                      {formatDisplayText(td("produto", String(item.produto_id), "nome", item.nome_produto))}
                      {item.observacao ? (
                        <span style={{ display: "block", fontSize: "0.75rem", color: "var(--cardapio-muted)" }}>{item.observacao}</span>
                      ) : null}
                    </span>
                    <span className="cardapio-conta-item-price">{formatBRL(item.subtotal)}</span>
                  </label>
                </div>
              ))
            )}
          </div>
        )}
        <div className="cardapio-conta-summary">
          <div className="cardapio-conta-line">
            <span>{t("common.subtotal")}</span>
            <span>{formatBRL(subtotal)}</span>
          </div>
          <div className="cardapio-conta-line total">
            <span>{t("common.total")}</span>
            <span>{formatBRL(total)}</span>
          </div>
          <button
            type="button"
            className="cardapio-btn-primary"
            disabled={!atendimentoVinculo || contaVazia || selectedContaIds.size === 0 || total <= 0 || sending}
            onClick={() => void handleFecharConta()}
          >
            {t("account.closeMine")}
          </button>
        </div>
      </CardapioShell>
    );
  }
  return (
    <div className="cardapio-page">
      <div className="cardapio-inner cardapio-inner--nav-pad cardapio-layout">
        <aside ref={sidebarRef} className="cardapio-sidebar" aria-label="Categorias">
          {(catalogo?.categorias ?? []).map((cat) => (
            <SidebarCategoryCard
              key={cat.nome}
              displayName={td("categoria", categoryEntityId(cat.nome), "nome", cat.nome)}
              active={activeCategory === cat.nome}
              buttonRef={(node) => setSidebarCategoryRef(cat.nome, node)}
              onClick={() => scrollToCategory(cat.nome)}
            />
          ))}
        </aside>

        <div className="cardapio-main-panel">
          <header className="cardapio-header">
            <h1 className="cardapio-header-title">{empresaNome}</h1>
            <div className="cardapio-header-actions">
              <div className={`cardapio-lang${translating ? " cardapio-lang--loading" : ""}`}>
                <div className={`cardapio-flags${translating ? " cardapio-flags--loading" : ""}`}>
                  {flagLocales.map(({ code, src, label }) => (
                    <button
                      key={code}
                      type="button"
                      className={`cardapio-flag${locale === code ? " cardapio-flag--active" : ""}`}
                      aria-label={label}
                      aria-pressed={locale === code}
                      disabled={translating && code !== "pt"}
                      onClick={() => void selectLocale(code)}
                    >
                      <img src={src} alt="" />
                    </button>
                  ))}
                  {translating ? <Loader2 className="cardapio-flags-spinner animate-spin" aria-hidden /> : null}
                </div>
                <div className="cardapio-lang-dropdown">
                  <button
                    type="button"
                    className="cardapio-lang-current"
                    aria-label={currentFlagLocale.label}
                    aria-expanded={languageMenuOpen}
                    disabled={translating}
                    onClick={() => setLanguageMenuOpen((open) => !open)}
                  >
                    <img src={currentFlagLocale.src} alt="" />
                    <ChevronDown className="cardapio-lang-chevron" aria-hidden />
                  </button>
                  {languageMenuOpen ? (
                    <div className="cardapio-lang-menu">
                      {flagLocales.map(({ code, src, label }) => (
                        <button
                          key={code}
                          type="button"
                          className={`cardapio-lang-option${locale === code ? " cardapio-lang-option--active" : ""}`}
                          disabled={translating && code !== "pt"}
                          onClick={() => {
                            setLanguageMenuOpen(false);
                            void selectLocale(code);
                          }}
                        >
                          <img src={src} alt="" />
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
              {mesaRotaNormalizada ? <span className="cardapio-header-mesa">{t("account.tableMode")} {mesaRotaNormalizada}</span> : null}
            </div>
          </header>

          <nav className="cardapio-mobile-categories" aria-label="Categorias">
            {(catalogo?.categorias ?? []).map((cat) => {
              const nome = td("categoria", categoryEntityId(cat.nome), "nome", cat.nome);
              return (
                <button
                  key={cat.nome}
                  type="button"
                  className="cardapio-mobile-category"
                  aria-label={nome}
                  aria-controls={categorySectionId(cat.nome)}
                  aria-expanded={expandedCats.has(cat.nome)}
                  onClick={() => {
                    setExpandedCats((current) => new Set(current).add(cat.nome));
                    requestAnimationFrame(() => scrollToCategory(cat.nome));
                  }}
                >
                  <span className="cardapio-mobile-category-circle">
                    {cat.banner_imagem ? <img src={cat.banner_imagem} alt="" /> : null}
                  </span>
                  <span className="cardapio-mobile-category-name">{nome.trim().split(/\s+/u).pop()}</span>
                </button>
              );
            })}
          </nav>

          <main className="cardapio-content">
            {(catalogo?.promocoes ?? []).length > 0 ? (
              <section className="cardapio-promo">
                <div className="cardapio-promo-bar">{t("promo.section")}</div>
                <div className="cardapio-promo-list">
                  {(catalogo?.promocoes ?? []).map((promo) => (
                    <div
                      key={promo.id}
                      className="cardapio-promo-item cardapio-promo-item--clickable"
                      role="button"
                      tabIndex={0}
                      onClick={() => openPromo(promo)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openPromo(promo);
                        }
                      }}
                    >
                      <p className="cardapio-promo-title">
                        {formatDisplayText(td("promocao", String(promo.id), "nome", promo.nome))}
                      </p>
                      <p className="cardapio-promo-price">{formatBRL(promo.valor)}</p>
                      {promo.subtitulo ? (
                        <p className="cardapio-promo-sub">
                          {formatDisplayText(td("promocao", String(promo.id), "subtitulo", promo.subtitulo))}
                        </p>
                      ) : (
                        <span className="cardapio-promo-sub-spacer" aria-hidden />
                      )}
                      <CardapioAddBtn
                        className="cardapio-promo-add-btn"
                        label={t("promo.add")}
                        onClick={(e) => {
                          e.stopPropagation();
                          openPromo(promo);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {(catalogo?.categorias ?? []).map((cat) => {
              const expanded = expandedCats.has(cat.nome);
              const hasBannerImg = Boolean(cat.banner_imagem);
              const primary = clientConfig.primary_color || "#111";
              const catDisplayNome = td("categoria", categoryEntityId(cat.nome), "nome", cat.nome);
              return (
                <section
                  key={cat.nome}
                  id={categorySectionId(cat.nome)}
                  ref={(node) => {
                    if (node) categorySectionRefs.current.set(cat.nome, node);
                    else categorySectionRefs.current.delete(cat.nome);
                  }}
                  data-category-name={cat.nome}
                  className="cardapio-category cardapio-category-section"
                >
                  <button
                    type="button"
                    className={`cardapio-category-banner cardapio-category-banner--mobile gradient${hasBannerImg ? " cardapio-category-banner--image" : ""}`}
                    style={hasBannerImg ? undefined : { background: `linear-gradient(90deg, ${primary} 0%, ${primary}33 100%)` }}
                    onClick={() => toggleCategory(cat.nome)}
                  >
                    {hasBannerImg ? (
                      <>
                        <img src={cat.banner_imagem!} alt="" className="cardapio-category-banner-img" />
                        <span className="cardapio-category-banner-overlay" aria-hidden />
                      </>
                    ) : null}
                    {!hasBannerImg && empresaLogo ? (
                      <img src={empresaLogo} alt="" className="cardapio-category-banner-logo" />
                    ) : null}
                    <h2 className="cardapio-category-name">{formatDisplayText(catDisplayNome)}</h2>
                  </button>
                  <div className="cardapio-category-section-header">
                    <h2 className="cardapio-category-section-title">{formatDisplayText(catDisplayNome)}</h2>
                  </div>
                  <div className={`cardapio-products${expanded ? " is-expanded" : ""}`}>
                    {sortCardapioProdutosPorPreco(cat.produtos).map((p) => (
                      <ProductRow
                        key={p.id}
                        produto={p}
                        displayNome={td("produto", String(p.id), "nome", p.nome)}
                        displayDescricao={p.descricao ? td("produto", String(p.id), "descricao", p.descricao) : null}
                        addLabel={t("product.add")}
                        onOpen={() => openProduct(p)}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </main>
        </div>
      </div>

      <nav className="cardapio-nav" aria-label="Navegação cardápio">
        <button type="button" className="cardapio-nav-btn" onClick={openGarcom}>
          <CardapioGarcomIcon className="cardapio-nav-icon" />
          {t("nav.waiter")}
        </button>
        <div className="cardapio-nav-center">
          {cartCount > 0 ? <span className="cardapio-nav-badge">{cartCount}</span> : null}
          <button type="button" className="cardapio-nav-logo" onClick={() => setView("cart")} aria-label={t("nav.order")}>
            {empresaLogo ? (
              <img src={empresaLogo} alt="" />
            ) : (
              <span className="cardapio-nav-logo-fallback">SUA LOGO AQUI</span>
            )}
          </button>
          <span className="cardapio-nav-label">{t("nav.order")}</span>
        </div>
        <button type="button" className="cardapio-nav-btn" onClick={openConta}>
          <CardapioContaIcon className="cardapio-nav-icon" />
          {t("nav.account")}
        </button>
      </nav>

      {modalItem ? (
        <div className="cardapio-overlay">
          <div className="cardapio-modal cardapio-modal--product">
            {modalItem.imagem_url ? (
              <img src={modalItem.imagem_url} alt="" className="cardapio-product-modal-img" />
            ) : null}
            <h2 className="cardapio-modal-title">{formatDisplayText(modalDisplay?.nome ?? modalItem.nome)}</h2>
            {modalDisplay?.descricao ?? modalItem.descricao ? (
              <p className="cardapio-modal-sub">
                {formatDisplayText(modalDisplay?.descricao ?? modalItem.descricao ?? "")}
              </p>
            ) : null}
            <div className="cardapio-qty-row">
              <span style={{ fontWeight: 600 }}>{labelQuantidadeCaixa(modalItem.unidade_venda)}</span>
              <QtyControl
                value={modalQty}
                min={unidadeVendaPermiteDecimal(modalItem.unidade_venda) ? 0.01 : 1}
                max={modalItem.max_quantidade}
                permiteDecimal={unidadeVendaPermiteDecimal(modalItem.unidade_venda)}
                onChange={setModalQty}
                decreaseLabel={t("common.decrease")}
                increaseLabel={t("common.increase")}
              />
            </div>
            <p className="cardapio-obs-label">
              <MessageCircle className="h-4 w-4" /> {t("product.obsLabel")}
            </p>
            <input
              className="cardapio-modal-obs-input"
              placeholder={t("product.obsPlaceholder")}
              value={modalObs}
              onChange={(e) => setModalObs(e.target.value)}
            />
            <div className="cardapio-modal-actions">
              <button type="button" className="cardapio-btn-outline" onClick={() => setModalItem(null)}>
                <ChevronLeft className="h-4 w-4 inline" /> {t("common.back")}
              </button>
              <button type="button" className="cardapio-btn-add" onClick={addToCart}>
                <span>{t("product.add")}</span>
                <span>{formatBRL(modalItem.preco_unitario * modalQty)}</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {garcomModal === "info" ? (
        <div className="cardapio-overlay">
          <div className="cardapio-modal">
            <h2 className="cardapio-modal-title">{t("waiter.title")}</h2>
            <p className="cardapio-modal-sub">{t("waiter.subInfo")}</p>
            <div className="cardapio-field">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="g_nome">{t("common.name")}</label>
                <span className="text-xs text-zinc-400">{garcomNome.length}/10</span>
              </div>
              <input
                id="g_nome"
                maxLength={10}
                value={garcomNome}
                onChange={(e) => setGarcomNome(filterNomeInput(e.target.value).slice(0, 10))}
              />
            </div>
            <div className="cardapio-field">
              <label htmlFor="g_mesa">{t("common.tableNumber")}</label>
              <input
                id="g_mesa"
                inputMode="numeric"
                maxLength={limiteAtendimentoNumero(modo)}
                value={garcomMesa}
                onChange={(e) => setGarcomMesa(filterMesaInput(e.target.value, modo))}
                onBlur={() => setGarcomMesa(normalizeMesaDigits(garcomMesa))}
              />
            </div>
            <div className="cardapio-modal-footer-actions">
              <button type="button" className="cardapio-btn-outline" onClick={() => setGarcomModal(null)}>
                <ChevronLeft className="h-4 w-4 inline" /> {t("common.back")}
              </button>
              <button
                type="button"
                className="cardapio-btn-primary"
                disabled={!garcomIdentificacaoValida}
                onClick={() => {
                  const mesa = normalizeMesaDigits(garcomMesa);
                  const nome = garcomNome.trim();
                  if (!garcomIdentificacaoValida) {
                    toast.error(t("errors.fillAllFields"));
                    return;
                  }
                  saveCliente({ nome, whatsapp: whatsappDigits(cliente.whatsapp) }, storageScope);
                  setCliente((cur) => ({ ...cur, nome }));
                  setGarcomMesa(mesa);
                  setGarcomModal("options");
                }}
              >
                {t("common.continue")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {garcomModal === "options" ? (
        <div className="cardapio-overlay">
          <div className="cardapio-modal">
            <h2 className="cardapio-modal-title">{t("waiter.title")}</h2>
            <p className="cardapio-modal-sub">{t("waiter.subOptions")}</p>
            <div className="cardapio-garcom-list">
              {([
                { key: "waiter.extraGlass" as const, value: garcomCopos, set: setGarcomCopos },
                { key: "waiter.extraCutlery" as const, value: garcomTalheres, set: setGarcomTalheres },
                { key: "waiter.extraPlate" as const, value: garcomPratos, set: setGarcomPratos },
              ]).map((row) => (
                <div key={row.key} className="cardapio-garcom-row">
                  <span>{t(row.key)}</span>
                  <QtyControl
                    value={row.value}
                    min={0}
                    onChange={row.set}
                    decreaseLabel={t("common.decrease")}
                    increaseLabel={t("common.increase")}
                  />
                </div>
              ))}
              <div className="cardapio-garcom-row">
                <span>{t("waiter.clearTable")}</span>
                <label>
                  <input
                    type="checkbox"
                    className="cardapio-checkbox"
                    checked={garcomLimpar}
                    onChange={(e) => setGarcomLimpar(e.target.checked)}
                  />{" "}
                  {t("waiter.markOption")}
                </label>
              </div>
            </div>
            <div className="cardapio-field">
              <label htmlFor="g_outros">{t("waiter.otherLabel")}</label>
              <textarea id="g_outros" placeholder={t("waiter.otherPlaceholder")} value={garcomOutros} onChange={(e) => setGarcomOutros(e.target.value)} />
            </div>
            <div className="cardapio-modal-footer-actions">
              <button type="button" className="cardapio-btn-outline" onClick={() => setGarcomModal("info")}>
                <ChevronLeft className="h-4 w-4 inline" /> {t("common.back")}
              </button>
              <button
                type="button"
                className="cardapio-btn-primary"
                disabled={garcomSending || !garcomIdentificacaoValida}
                onClick={async () => {
                  const mesa = normalizeMesaDigits(garcomMesa);
                  const nome = garcomNome.trim();
                  const temOpcao =
                    garcomCopos > 0 ||
                    garcomTalheres > 0 ||
                    garcomPratos > 0 ||
                    garcomLimpar ||
                    garcomOutros.trim() !== "";
                  if (garcomSending || !garcomIdentificacaoValida) {
                    toast.error(t("errors.fillGarcom"));
                    return;
                  }
                  if (!temOpcao) {
                    toast.error(t("errors.fillGarcom"));
                    return;
                  }
                  setGarcomSending(true);
                  try {
                    await cardapioApi.solicitarGarcom({
                      cliente_nome: nome,
                      mesa,
                      comanda_id: atendimentoVinculo?.comanda_id,
                      idempotency_key: createIdempotencyKey(),
                      opcoes: {
                        copos: garcomCopos,
                        talheres: garcomTalheres,
                        pratos: garcomPratos,
                        limpar_mesa: garcomLimpar,
                        observacao: garcomOutros.trim(),
                      },
                    });
                    setGarcomCopos(0);
                    setGarcomTalheres(0);
                    setGarcomPratos(0);
                    setGarcomLimpar(false);
                    setGarcomOutros("");
                    setGarcomModal(null);
                    setView("garcom-sent");
                  } catch (e) {
                    toast.error(e instanceof Error ? e.message : t("errors.fillGarcom"));
                  } finally {
                    setGarcomSending(false);
                  }
                }}
              >
                {garcomSending ? <Loader2 className="h-4 w-4 animate-spin inline" /> : null}
                {t("waiter.request")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ProductRow({
  produto: p,
  displayNome,
  displayDescricao,
  addLabel,
  onOpen,
}: {
  produto: CardapioProduto;
  displayNome: string;
  displayDescricao: string | null;
  addLabel: string;
  onOpen: () => void;
}) {
  const hasImg = Boolean(p.imagem_url);
  const hasDescricao = Boolean(displayDescricao?.trim());
  const descricaoLista = hasDescricao ? formatDisplayText(displayDescricao ?? "") : "";
  const descricaoListaLimitada =
    descricaoLista.length > 100 ? `${descricaoLista.slice(0, 100).trimEnd()}...` : descricaoLista;

  return (
    <div
      className={`cardapio-product-row cardapio-product-row--clickable${hasImg ? " cardapio-product-row--with-img" : ""}${hasDescricao ? "" : " cardapio-product-row--no-desc"}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      {hasImg ? (
        <div className="cardapio-product-list-thumb-wrap">
          <img src={p.imagem_url!} alt="" className="cardapio-product-list-thumb" />
        </div>
      ) : null}
      <div className="cardapio-product-info">
        <p className="cardapio-product-name">{formatDisplayText(displayNome)}</p>
        {hasDescricao ? <p className="cardapio-product-desc">{descricaoListaLimitada}</p> : null}
      </div>
      <div className="cardapio-product-side">
        <span className="cardapio-product-price">{formatBRL(p.preco_venda ?? 0)}</span>
        <CardapioAddBtn
          label={addLabel}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        />
      </div>
    </div>
  );
}





